import type React from 'react';
import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { type CloudResult } from 'repository/repository';
import { type Sms } from 'typedefs/sms';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import SendMessageDialog, { type SendMessageParams } from './SendMessageDialog';

const SMS_CHARACTER_LIMIT = 160;

interface SendSMSDialogProps {
  isOpen?: boolean;
  participants: ParticipantInterface[];
  onClose: () => void;
  onExited?: () => void;
  loading?: boolean;
  onSendSms(message: string, selectedPlayersIds: string[]): Promise<CloudResult<Sms>>;
}

const SendSMSDialog: React.FC<React.PropsWithChildren<SendSMSDialogProps>> = ({ onSendSms, ...props }) => {
  const { t } = useTranslation();
  const handleSendSMS = useCallback(
    async (params: SendMessageParams) => {
      const res = await onSendSms(params.message, params.personIds);
      if (res) {
        const successMessages = res.data?.sendSMS?.some((contact) => contact.sentPhoneNumbers?.length > 0);
        if (successMessages) toast.success(t('messageSendSuccessfully'));
      }
    },
    [onSendSms, t],
  );

  return (
    <SendMessageDialog
      {...props}
      withSafePlayWarning
      characterLimit={SMS_CHARACTER_LIMIT}
      showParticipants={props.participants?.length > 1}
      title={t('sendSMS')}
      onSend={handleSendSMS}
    />
  );
};

export default SendSMSDialog;
